import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useCommunications } from '../../../hooks/useCommunications';
import type { Communication } from '../../../services/communications';
import { apiErrorMessage } from '../../../utils/apiError';
import { channelDef } from '../practice/communications/channels';
import { CommunicationStatusBadge } from '../practice/communications/CommunicationStatusBadge';
import { CategoryPillsCapped } from '../practice/communications/CategoryPillsCapped';
import { formatRelativeActivity } from '../../../utils/dates';
import { CardErrorState } from './CardErrorState';

// Accounts Overview hub's "Latest communications" card, generalized to span
// every practice instead of one.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full';
const LATEST_LIMIT = 5;
const SKELETON_ROW_COUNT = 3;

function ChannelTile({ communication }: { communication: Communication }) {
    const def = communication.lastEntry ? channelDef(communication.lastEntry.channel) : null;
    if (!def) {
        return (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
            </div>
        );
    }
    const Icon = def.icon;
    return (
        <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: def.soft, color: def.tone }}
        >
            <Icon className="h-3.5 w-3.5" />
        </div>
    );
}

interface HubCommunicationRowProps {
    communication: Communication;
    onOpen: () => void;
}

function HubCommunicationRow({ communication, onOpen }: HubCommunicationRowProps) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-full text-left px-5 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-start gap-3"
        >
            <ChannelTile communication={communication} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[13px] font-semibold text-gray-900 shrink-0 truncate max-w-[45%]">
                        {communication.title}
                    </span>
                    {communication.practiceName ? (
                        <span className="text-[12px] text-gray-500 truncate">
                            · {communication.practiceName}
                        </span>
                    ) : (
                        <span className="text-[12px] text-gray-400 truncate">· No practice</span>
                    )}
                    <span className="text-[11px] text-gray-400 shrink-0 ml-auto">
                        {formatRelativeActivity(communication.lastActivityAt)}
                    </span>
                </div>
                {communication.lastEntry && (
                    <p className="text-[13px] text-gray-600 mt-1 line-clamp-1">
                        Last by {communication.lastEntry.authorName}
                    </p>
                )}
                <div className="flex items-center gap-2 mt-1.5 min-w-0">
                    <CategoryPillsCapped categories={communication.categories} />
                    <CommunicationStatusBadge status={communication.status} />
                    <span className="font-mono text-[11px] text-gray-400 shrink-0">
                        {communication.referenceNo}
                    </span>
                </div>
            </div>
        </button>
    );
}

function SkeletonRow() {
    return (
        <div className="flex items-start gap-3 px-5 py-3">
            <div className="w-7 h-7 rounded-lg bg-gray-100 animate-pulse shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
                <div className="h-3 w-1/3 rounded bg-gray-100 animate-pulse" />
                <div className="h-2.5 w-2/3 rounded bg-gray-100 animate-pulse" />
            </div>
        </div>
    );
}

interface LatestCommunicationsCardProps {
    className?: string;
}

export function LatestCommunicationsCard({ className = '' }: LatestCommunicationsCardProps) {
    const navigate = useNavigate();
    const { communications, isLoading, isError, error, refresh } = useCommunications({
        page: 1,
        limit: LATEST_LIMIT
    });

    const goToCommunication = (communication: Communication) => {
        const path = communication.practiceId
            ? `/accounts/practices/${communication.practiceId}/communications/${communication.id}`
            : `/accounts/communications/${communication.id}`;
        navigate(path);
    };

    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <h3 className="text-[15px] font-bold text-gray-800">Latest communications</h3>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    All practices
                </span>
            </div>
            {isLoading ? (
                <div className="py-1 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : isError ? (
                <CardErrorState
                    message={apiErrorMessage(error, "Couldn't load latest communications")}
                    onRetry={() => void refresh()}
                />
            ) : communications.length === 0 ? (
                <div className="flex-1 min-h-0 px-6 pb-8 pt-4 flex flex-col items-center justify-center gap-3 text-center">
                    <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300" strokeWidth={1} />
                    <p className="text-sm text-gray-500">No communications yet.</p>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {communications.map((communication) => (
                        <HubCommunicationRow
                            key={communication.id}
                            communication={communication}
                            onOpen={() => goToCommunication(communication)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
